import { COMPONENT_VALUE_TYPES as types, SLICK_SLIDE_SPEED_IN_SECONDS } from 'shared/constants';
import type { PropsSchemaList } from 'shared/types/PropsSchema';

export interface Props {
  pageTitle: string;
  slides: {
    slideImage: {
      img: string;
      imgAlt: string;
    };
    slideInformation: {
      slideDescription: {
        title: string;
        details: string;
        dateTime: {
          date: string; // UTC date
          timezone: string;
        };
      },
      user: {
        userName: string;
        userDescription: string;
      };
      link: {
        text: string;
        url: string;
      };
    };
  }[];
  events: {
    eventImage: {
      enableLiveIcon: boolean;
      image: {
        img: string;
        imgAlt: string;
      };
      imageTextOverlay: {
        title: string;
        enableImageCountdown: boolean;
        videoLength: string;
        button: {
          text: string;
          url: string;
        };
      };
      videoTextOverlay: {
        videoPlayIcon: boolean;
        videoLink: string;
      };
    };
    eventInformation: {
      eventDescription: {
        title: string;
        details: string;
        dateTime: {
          date: string; // UTC date
          timezone: string;
        };
      },
      user: {
        userName: string;
        userDescription: string;
      };
      link: {
        url: string;
      };
    },
  }[];
  searchBar: {
    searchPlaceholderText: string;
    searchErrorMessageText: string;
    searchDropdownTitleText: string;
    dropdown: {
      dropdownShowAllText: string;
      dropdownList: {
        text: string;
      }[];
    };
  };
  additionalSettings: {
    navigation: {
      enableTopNavigation: boolean;
    };
    slides: {
      slideSpeedInSeconds: number;
    };
    events: {
      eventListLoadMoreButton: string;
    };
  };
  confluence: string;
}

const propsSchema: PropsSchemaList = [
  {
    key: 'pageTitle',
    label: 'Page Title',
    type: types.TEXT,
  },
  {
    key: 'slides',
    label: 'Slides',
    type: types.ARRAY,
    children: [
      {
        key: 'slideImage',
        label: 'Slide Image',
        type: types.OBJECT,
        children: [
          {
            key: 'img',
            label: 'Image',
            type: types.S3_ASSET_URL,
          },
          {
            key: 'imgAlt',
            label: 'Image Alternative Text (A brief description of the image to optimize search engine visibility)',
            type: types.TEXT,
          },
        ],
      },
      {
        key: 'slideInformation',
        label: 'Slide Information',
        type: types.OBJECT,
        children: [
          {
            key: 'slideDescription',
            label: 'Slide Description',
            type: types.OBJECT,
            children: [
              {
                key: 'title',
                label: 'Title',
                type: types.TEXT,
              },
              {
                key: 'details',
                label: 'Details',
                type: types.TEXT,
              },
              {
                key: 'dateTime',
                label: 'Event Date',
                type: types.DATETIME,
              },
            ],
          },
          {
            key: 'user',
            label: 'User Information',
            type: types.OBJECT,
            children: [
              {
                key: 'userName',
                label: 'User Name',
                type: types.TEXT,
              },
              {
                key: 'userDescription',
                label: 'User Description',
                type: types.TEXT,
              },
            ],
          },
          {
            key: 'link',
            label: 'Link',
            type: types.OBJECT,
            children: [
              {
                key: 'text',
                label: 'Text',
                type: types.TEXT,
              },
              {
                key: 'url',
                label: 'URL',
                type: types.TEXT,
              },
            ]
          },
        ],
      },
    ]
  },
  {
    key: 'events',
    label: 'Events',
    type: types.ARRAY,
    children: [
      {
        key: 'eventImage',
        label: 'Event Image',
        type: types.OBJECT,
        children: [
          {
            key: 'enableLiveIcon',
            label: 'Enable Live Icon',
            type: types.BOOLEAN,
          },
          {
            key: 'image',
            label: 'Event Image',
            type: types.OBJECT,
            children: [
              {
                key: 'img',
                label: 'Image',
                type: types.S3_ASSET_URL,
              },
              {
                key: 'imgAlt',
                label: 'Image Alternative Text (A brief description of the image to optimize search engine visibility)',
                type: types.TEXT,
              },
            ],
          },
          {
            key: 'imageTextOverlay',
            label: 'Image Text Overlay',
            type: types.OBJECT,
            tooltip: 'Text that is displayed over the image',
            children: [
              {
                key: 'enableImageCountdown',
                label: 'Enable Image Countdown (Countdown time is based on Event Description Date)',
                type: types.BOOLEAN,
              },
              {
                key: 'title',
                label: 'Image text',
                type: types.TEXT,
              },
              {
                key: 'button',
                label: 'Event Image Button',
                type: types.OBJECT,
                children: [
                  {
                    key: 'text',
                    label: 'Text',
                    type: types.TEXT,
                  },
                  {
                    key: 'url',
                    label: 'URL',
                    type: types.TEXT,
                  },
                ]
              },
            ],
          },
          {
            key: 'videoTextOverlay',
            label: 'Video Text Overlay',
            type: types.OBJECT,
            children: [
              {
                key: 'videoPlayIcon',
                label: 'Show the Play Icon',
                type: types.BOOLEAN,
              },
              {
                key: 'videoLink',
                label: 'Video Link (Add the video link to display the video length)',
                type: types.TEXT,
              },
            ],
          },
        ],
      },
      {
        key: 'eventInformation',
        label: 'Event Information',
        type: types.OBJECT,
        children: [
          {
            key: 'eventDescription',
            label: 'Event Description',
            type: types.OBJECT,
            children: [
              {
                key: 'title',
                label: 'Title',
                type: types.TEXT,
              },
              {
                key: 'details',
                label: 'Event Details',
                type: types.TEXT,
              },
              {
                key: 'dateTime',
                label: 'Event Date',
                type: types.DATETIME
              },
            ],
          },
          {
            key: 'user',
            label: 'User Information',
            type: types.OBJECT,
            children: [
              {
                key: 'userName',
                label: 'User Name',
                type: types.TEXT,
              },
              {
                key: 'userDescription',
                label: 'User Description',
                type: types.TEXT,
              },
            ],
          },
          {
            key: 'link',
            label: 'Link',
            type: types.OBJECT,
            children: [
              {
                key: 'url',
                label: 'URL',
                type: types.TEXT,
              },
            ]
          },
        ],
      },
    ],
  },
  {
    key: 'searchBar',
    label: 'Search & Dropdown',
    type: types.OBJECT,
    children: [
      {
        key: 'searchPlaceholderText',
        label: 'Search Placeholder Text',
        type: types.TEXT,
      },
      {
        key: 'searchErrorMessageText',
        label: 'Search Error Message Text',
        type: types.TEXT,
      },
      {
        key: 'searchDropdownTitleText',
        label: 'Dropdown Title Text',
        type: types.TEXT,
      },
      {
        key: 'dropdown',
        label: 'Dropdown Text',
        type: types.OBJECT,
        children: [
          {
            key: 'dropdownShowAllText',
            label: 'Dropdown Show All Text',
            type: types.TEXT,
          },
          {
            key: 'dropdownList',
            label: 'Dropdown List',
            type: types.ARRAY,
            children: [
              {
                key: 'text',
                label: 'Text',
                type: types.TEXT,
              },
            ],
          },
        ]
      },
    ],
  },
  {
    key: 'additionalSettings',
    label: 'Additional Settings',
    type: types.OBJECT,
    children: [
      {
        key: 'navigation',
        label: 'Top Navigation Settings',
        type: types.OBJECT,
        children: [
          {
            key: 'enableTopNavigation',
            label: 'Show search navigation at the top when scrolling down',
            type: types.BOOLEAN,
          },
        ]
      },
      {
        key: 'slides',
        label: 'Slides Settings',
        type: types.OBJECT,
        children: [
          {
            key: 'slideSpeedInSeconds',
            label: 'Slide Show Speed (Seconds)',
            type: types.ENUM,
            options: SLICK_SLIDE_SPEED_IN_SECONDS,
          },
        ]
      },
      {
        key: 'events',
        label: 'Events Settings',
        type: types.OBJECT,
        children: [
          {
            key: 'eventListLoadMoreButton',
            label: 'Event List Load More Button Text',
            type: types.TEXT,
          },
        ]
      },
    ]
  },
  {
    key: 'confluence',
    label: 'Confluence Event List Widget Guide',
    type: types.TEXT,
    tooltip: 'The documentation for this widget can be found at https://confluence.faradayfuture.com/display/FW/FAQ+Widget',
  },
];

// mr.prop >:(
const defaultProps: Props = {
  pageTitle: 'Live & Events',
  slides: [
    {
      slideImage: {
        img: 'https://genesis-cdn.ff.com/assets/pages/live-and-events/default-images/start-production-default.jpg',
        imgAlt: '3 people smiling with the Faraday Future Logo in the background'
      },
      slideInformation: {
        slideDescription: {
          title: 'Faraday Future Announces Plans to Start Production of FF 91 Futurist in March 2023, Outlines Financing Progress and Completion of Product Upgrades',
          details: 'Live Time :',
          dateTime: {
            date: '2022-12-14T08:00:00Z',
            timezone: 'America/Los_Angeles',
          },
        },
        user: {
          userName: 'Faraday Future',
          userDescription: 'Official Information',
        },
        link: {
          text: 'Read More >',
          url: 'https://www.ff.com/us/press-room/FF-announces-plan-to-start-of-production-of-FF91-Futurist-in-March-2023/',
        }
      },
    },
    {
      slideImage: {
        img: 'https://genesis-cdn.ff.com/press_room/2023-08/0818PR/original/0821Home-PCV2-default.jpg',
        imgAlt: 'FF 91 2.0 Futurist parked by a Lake'
      },
      slideInformation: {
        slideDescription: {
          title: 'Faraday Future Developer Co-Creation Festival',
          details: 'Ongoing Event',
          dateTime: {
            date: '',
            timezone: '',
          },
        },
        user: {
          userName: 'Faraday Future',
          userDescription: 'Official Information',
        },
        link: {
          text: 'Read More >',
          url: 'https://www.ff.com/us/developer-co-creation-festival/',
        }
      },
    },
  ],
  events: [
    {
      eventImage: {
        enableLiveIcon: false,
        image: {
          img: 'https://genesis-cdn.ff.com/assets/pages/live-and-events/default-images/fflogo11-default.jpg',
          imgAlt: 'FF Logo',
        },
        imageTextOverlay: {
          title: 'html:Live in Mar. 29th <br /> 19:00 - 22:00',
          enableImageCountdown: true,
          videoLength: '',
          button: {
            text: '+ Reserve',
            url: 'https://www.ff.com/us/design/2022/FF91/model',
          },
        },
        videoTextOverlay: {
          videoPlayIcon: false,
          videoLink: '',
        },
      },
      eventInformation: {
        eventDescription: {
          title: 'Faraday Future Announces Comprehensive Governance Resolution with Major Shareholders and New Financing',
          details: 'Live time :',
          dateTime: {
            date: '2024-01-01T08:00:00Z',
            timezone: 'America/Los_Angeles',
          },
        },
        user: {
          userName: 'Faraday Future',
          userDescription: 'Official Information',
        },
        link: {
          url: 'https://www.ff.com',
        },
      },
    },
    {
      eventImage: {
        enableLiveIcon: true,
        image: {
          img: 'https://genesis-cdn.ff.com/assets/pages/live-and-events/default-images/welcome-live-default.jpg',
          imgAlt: '919 Futurist Day Event',
        },
        imageTextOverlay: {
          title: '',
          enableImageCountdown: false,
          videoLength: '',
          button: {
            text: '',
            url: '',
          },
        },
        videoTextOverlay: {
          videoPlayIcon: false,
          videoLink: '',
        },
      },
      eventInformation: {
        eventDescription: {
          title: 'Welcome to the FF 91 Futurist Final Launch',
          details: '3,241 views on Live',
          dateTime: {
            date: '',
            timezone: '',
          },
        },
        user: {
          userName: 'Faraday Future',
          userDescription: 'Official Information',
        },
        link: {
          url: 'https://www.ff.com',
        },
      },
    },
    {
      eventImage: {
        enableLiveIcon: false,
        image: {
          img: 'https://genesis-cdn.ff.com/assets/pages/live-and-events/default-images/ff-sunset-default.jpg',
          imgAlt: 'FF 91 with a sunset backdrop',
        },
        imageTextOverlay: {
          title: '',
          enableImageCountdown: false,
          videoLength: '',
          button: {
            text: '',
            url: '',
          },
        },
        videoTextOverlay: {
          videoPlayIcon: true,
          videoLink: 'https://ff-genesis-cdn-dev.s3.amazonaws.com/FF_FactoryWalkThrough2021.11_Edit3.0_2021.11.14.mp4',
        },
      },
      eventInformation: {
        eventDescription: {
          title: 'Faraday Future Announces Comprehensive Governance Resolution with Major Shareholders and New Financing',
          details: '3,241 views',
          dateTime: {
            date: '2023-01-29T08:00:00Z',
            timezone: 'America/Los_Angeles',
          },
        },
        user: {
          userName: 'Kristen Jones',
          userDescription: 'Official Information',
        },
        link: {
          url: 'https://www.ff.com',
        },
      },
    },
  ],
  searchBar: {
    searchPlaceholderText: 'Search',
    searchErrorMessageText: '"No search results found"',
    searchDropdownTitleText: 'All Time',
    dropdown: {
      dropdownShowAllText: 'All',
      dropdownList: [
        {
          text: '2023',
        },
        {
          text: '2022',
        },
        {
          text: '2021',
        },
        {
          text: '2020',
        },
        {
          text: '2019',
        },
        {
          text: '2018',
        },
        {
          text: '2017',
        },
        {
          text: '2016',
        },
      ]
    }
  },
  additionalSettings: {
    navigation: {
      enableTopNavigation: true,
    },
    slides: {
      slideSpeedInSeconds: 5,
    },
    events: {
      eventListLoadMoreButton: 'Load More',
    },
  },
  confluence: 'https://confluence.faradayfuture.com/display/FW/Event+List'
}

export default {
  title: 'Event List Page',
  id: 'EventList',
  propsSchema,
  defaultProps,
}
